import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import CheckIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { Theme } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Fade from '@mui/material/Fade';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useFetcher, useNavigation } from '@remix-run/react';
import * as React from 'react';
import Image from 'remix-image';
import { Color } from '~/components/shared/Colors';
import { MuiRemixLink } from '~/components/shared/links';
import NoImageAvailable from '~/images/no_image_available.svg';
import type { Color as TColor } from '~/services/api-utils.server';
import type { Cart as TCart, CartItem, CartItemPackPart, CartItemProductPart } from '~/services/api.server';
import {
  getCartItemId,
  getCartItemIdWithoutSize,
  getCartItemPartId,
  getCartPartsLength,
  getFormattedPrice,
  isCartItemPack,
  isCartItemProduct,
} from '~/utils/common';
import { ellipsisStyle, scrollbarStyle } from '~/utils/common-styles';
import { ConfigurationTypes } from '~/utils/constants';
import { LightTooltip } from './Cart/common';
import DraggableCarousel from './Shop/DraggableCarousel';

export const cartWidth = 110;

type CartProps = {
  cart: TCart;
  open: boolean;
  setOpenCart: React.Dispatch<React.SetStateAction<boolean>>;
};
export default function Cart({ cart, open, setOpenCart }: CartProps) {
  const [anchor, setAnchor] = React.useState(false);
  const [productDrawer, setProductDrawer] = React.useState<CartItem | null>(null);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const hasVerticalScroll = (containerRef?.current?.scrollHeight || 0) > (containerRef?.current?.clientHeight || 0);

  const cartTotal = cart?.products?.reduce((total, cartItem) => {
    return total + getCartItemTotalQuantity(cartItem) * cartItem.product.prices.price.amount;
  }, 0);

  // Syncs productDrawer with cart updates
  React.useEffect(() => {
    if (productDrawer !== null) {
      const updated = cart.products.find(
        (product) => getCartItemIdWithoutSize(product) === getCartItemIdWithoutSize(productDrawer)
      );
      setProductDrawer(updated ?? null);
      setAnchor(true);
    } else {
      setAnchor(false);
    }
  }, [cart, productDrawer]);

  const productCount = React.useRef(getCartPartsLength(cart) ?? 0);
  React.useEffect(() => {
    if (cart?.products?.length === 0) {
      setOpenCart(false);
    } else if (getCartPartsLength(cart) > productCount.current) {
      setOpenCart(true);
    }
    productCount.current = getCartPartsLength(cart) ?? 0;
  }, [cart, setOpenCart]);

  const handleOpenDetails = (cartItem: CartItem) => {
    setProductDrawer(cartItem);
    if (!anchor) {
      setAnchor(true);
    }
  };

  const handleCloseDrawer = () => {
    setAnchor(false);
    setProductDrawer(null);
  };

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      anchor="right"
      open={open}
      sx={{
        zIndex: { xs: '1301', md: 'inherit' },
        '& .MuiDrawer-paper': {
          width: { xs: '100%', md: hasVerticalScroll ? cartWidth + 5 : cartWidth },
          overflow: 'visible',
          zIndex: '1302 !important',
        },
      }}
    >
      {!isMobile && (
        <>
          <Box
            key="desktop"
            sx={{
              p: '16px 8px',
              borderBottom: '2px solid #E0E0E0',
              textAlign: 'center',
              position: 'relative',
              '&:after': !anchor
                ? {
                    content: '""',
                    position: 'absolute',
                    width: '10px',
                    height: '10px',
                    borderTop: '0px solid #E0E0E0',
                    borderRight: '0px solid #E0E0E0',
                    borderBottom: '1px solid #E0E0E0',
                    borderLeft: '1px solid #E0E0E0',
                    top: '35%',
                    left: '0',
                    marginTop: '-5px',
                    marginLeft: '-6px',
                    transform: 'rotate(45deg)',
                    background: 'white',
                  }
                : {},
            }}
          >
            <Typography variant="body2" color="text.primary" fontWeight={500}>
              Total
            </Typography>
            <Typography variant="body2" mb={1} fontSize={14} sx={ellipsisStyle}>
              {getFormattedPrice(cartTotal / 100)}
            </Typography>
            <Button
              variant="contained"
              disabled={cart.products.length === 0}
              href="/cart"
              sx={{ fontSize: 13, py: 0.5, px: 1.25, fontWeight: 400 }}
            >
              Checkout
            </Button>
          </Box>
          <Stack
            alignItems="center"
            px={0.5}
            lineHeight="10px"
            sx={{
              backgroundColor: '#FAFAFA',
            }}
          >
            <Typography variant="caption" fontWeight={500} color="text.primary">
              Items
            </Typography>
          </Stack>
          <Box
            ref={containerRef}
            sx={{
              maxHeight: '100vh',
              width: hasVerticalScroll ? cartWidth + 4 : cartWidth,
              boxSizing: 'border-box',
              ...scrollbarStyle,
            }}
          >
            {cart.products.map((cartItem) => (
              <CartProduct
                key={getCartItemId(cartItem)}
                cartItem={cartItem}
                productDrawer={productDrawer}
                anchor={anchor}
                onOpenDetails={() => handleOpenDetails(cartItem)}
              />
            ))}
          </Box>
        </>
      )}

      {isMobile && (
        <Box
          key="mobile"
          sx={{ display: { xs: 'grid', md: 'none' }, gridTemplateRows: '72px 1fr 128px', height: '100dvh' }}
        >
          <Stack direction="row" alignItems="center" py={0} px={2}>
            <CloseIcon
              sx={{
                color: '#757575',
                cursor: 'pointer',
                width: '24px',
                height: '24px',
                marginRight: '24px',
              }}
              onClick={() => setOpenCart(false)}
            />
            <Typography variant="h6" sx={{ color: 'rgba(58, 60, 63, 1)' }}>
              Your Cart (
              <Typography variant="h6" component="span" sx={{ color: 'text.secondary' }}>
                {cart.products.length}
              </Typography>
              )
            </Typography>
          </Stack>
          <>
            {cart.products.length > 0 ? (
              <Box sx={{ px: 2, overflow: 'auto' }}>
                {cart.products.map((cartItem) => (
                  <CartProductMobile
                    key={getCartItemId(cartItem)}
                    cartItem={cartItem}
                    onOpenDetails={() => handleOpenDetails(cartItem)}
                  />
                ))}
              </Box>
            ) : (
              <Box sx={{ display: 'grid', placeItems: 'center', px: 4 }}>
                <Stack justifyContent="center" textAlign="center">
                  <img
                    src="https://swagcharm-static.s3.amazonaws.com/landing/cart/credit-card-1.png"
                    alt="Empty Basket"
                    width={150}
                    height={150}
                    style={{ marginBottom: '16px', marginInline: 'auto' }}
                  />
                  <Typography variant="h6" color="text.primary" fontWeight={700} mb={1}>
                    Your cart is empty
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Discover amazing products at our store and fill it up with your favorites.
                  </Typography>
                  <Button component={MuiRemixLink} href="/shop" fontSize={15}>
                    START EXPLORING NOW
                  </Button>
                </Stack>
              </Box>
            )}
          </>
          <Box sx={{ backgroundColor: '#FAFAFA', p: '24px 16px' }}>
            <Box sx={{ p: '0 8px', display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
              <Typography variant="subtitle1" sx={{ color: 'text.primary', mr: '24px' }}>
                Estimated Total
              </Typography>
              <Typography variant="subtitle1" sx={{ color: 'text.primary' }}>
                {getFormattedPrice(cartTotal / 100)}
              </Typography>
            </Box>
            <Button
              variant="contained"
              disabled={cart.products.length === 0}
              href="/cart"
              fullWidth
              sx={{ fontSize: '14px', fontWeight: 500, boxShadow: 'none' }}
            >
              Checkout
            </Button>
          </Box>
        </Box>
      )}

      <Drawer
        anchor="right"
        open={anchor}
        onClose={handleCloseDrawer}
        sx={{
          zIndex: { xs: '1500 !important', md: '1301 !important' },
          '& .MuiDrawer-paper': {
            right: { xs: '', md: hasVerticalScroll ? `${cartWidth + 5}px !important` : `${cartWidth}px !important` },
            width: { xs: '100%', md: 'auto' },
          },
        }}
      >
        {/* Desktop */}
        <Box
          sx={{
            p: '19px 24px',
            position: 'relative',
            maxHeight: '100vh',
            gridTemplateRows: '136px 1fr',
            gridGap: '20px',
            height: '100%',
            display: { xs: 'none', md: 'grid' },
          }}
        >
          <CloseIcon
            sx={{
              position: 'absolute',
              top: '8px',
              left: '12px',
              color: '#757575',
              cursor: 'pointer',
              width: '24px',
              height: '24px',
            }}
            onClick={handleCloseDrawer}
          />
          <VariantHeaderDesktop cartItem={productDrawer} />

          <Stack
            bgcolor="#F0F5F9"
            width="575px"
            height="100%"
            maxHeight="75vh"
            p={2}
            mb={6}
            borderRadius={2}
            spacing={2}
          >
            <Stack
              sx={{
                backgroundColor: 'white',
                p: 2,
                borderRadius: 2,
                height: '100%',
                '& > .MuiBox-root + .MuiBox-root': { pt: '16px', borderTop: '2px solid #EBEBEB' },
                ...scrollbarStyle,
              }}
            >
              {isCartItemPack(productDrawer) &&
                productDrawer.parts.map((variant, idx) => (
                  <VariantPack
                    product={productDrawer}
                    variant={variant}
                    position={idx + 1}
                    key={getCartItemPartId(productDrawer, variant)}
                  />
                ))}
              {isCartItemProduct(productDrawer) &&
                productDrawer.parts.map((variant) => (
                  <VariantProduct
                    product={productDrawer}
                    variant={variant}
                    key={getCartItemPartId(productDrawer, variant)}
                  />
                ))}
            </Stack>
            {/* TOFIX */}
            {/* {productDrawer?.configuration_type === ConfigurationTypes.decorated && (
              <Stack direction="row" justifyContent="flex-end">
                <Button variant="contained" type="submit" name="_action" value="addDesignLab" size="large">
                  {Object.keys(productDrawer?.mockup_images ?? {}).length > 0 ? 'EDIT DESIGN' : 'START DESIGNING'}
                </Button>
              </Stack>
            )} */}
          </Stack>
        </Box>
        {/* Mobile */}
        <VariantMobile product={productDrawer} setAnchor={setAnchor} />
      </Drawer>
    </Drawer>
  );
}

type CartProductProps = {
  cartItem: CartItem;
  anchor: boolean;
  onOpenDetails: () => void;
  productDrawer: CartItem | null;
};
function CartProduct({ cartItem, anchor, productDrawer, onOpenDetails }: CartProductProps) {
  const fetcher = useFetcher();

  return (
    <Box
      sx={{
        position: 'relative',
        p: 1,
        borderBottom: '1px solid #EBEBEB',
        textAlign: 'center',
        backgroundColor:
          anchor && productDrawer !== null && getCartItemId(cartItem) === getCartItemId(productDrawer) ? '#F0F5F9' : '',
      }}
    >
      <Avatar
        sx={{
          position: 'absolute',
          top: '3px',
          right: '15px',
          color: '#757575',
          backgroundColor: 'transparent',
          width: '20px',
          height: '20px',
          cursor: 'pointer',
        }}
      >
        <fetcher.Form method="post" action="/cart/?index">
          <input type="hidden" name="product" value={getCartItemId(cartItem)} />
          <input type="hidden" name="configuration_type" value={cartItem.configuration_type} />
          <input type="hidden" name="_action" value="delete" />
          <IconButton type="submit" aria-label="delete">
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </fetcher.Form>
      </Avatar>
      <Image
        src={
          cartItem.mockup_images?.[cartItem.product.id] ||
          cartItem.product.image ||
          cartItem.product.blank_image ||
          NoImageAvailable
        }
        width={56}
        height={56}
        alt="Cart Product"
        responsive={[{ size: { height: 56, width: 56 } }]}
        options={{ fit: 'inside' }}
        dprVariants={[1, 3]}
        loading="lazy"
      />
      {cartItem?.product.id === undefined || cartItem?.product.id < 0 ? (
        <Typography color="text.secondary" fontSize={12} mb={0.5} fontWeight={500}>
          {cartItem.product.name}
        </Typography>
      ) : (
        <MuiRemixLink
          href={`/shop/${cartItem.product.slug}`}
          color="inherit"
          underline="none"
          sx={{
            mb: 0.5,
            color: 'text.secondary',
            fontSize: 12,
            fontWeight: 500,
            display: 'block',
            '&:hover': { color: 'primary.main' },
          }}
        >
          {cartItem.product.name}
        </MuiRemixLink>
      )}
      <Typography
        sx={{
          mb: 1,
          color: 'text.primary',
          fontWeight: 500,
          fontSize: '12px',
          display: '-webkit-box',
          overflow: 'hidden',
          WebkitLineClamp: 1,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {getFormattedPrice(getCartItemTotalPrice(cartItem) / 100)}
      </Typography>
      <Button
        variant="outlined"
        sx={{
          fontSize: 13,
          display: 'block',
          margin: '0 auto',
          mt: 0.5,
          p: '4px 10px',
          letterSpacing: '0.15px',
          borderColor: 'primary.light',
        }}
        onClick={onOpenDetails}
      >
        Details
      </Button>
    </Box>
  );
}

export function CartProductMobile({ cartItem, onOpenDetails }: { cartItem: CartItem; onOpenDetails: () => void }) {
  const fetcher = useFetcher();

  return (
    <Box key={getCartItemId(cartItem)} width="100%" p={2} mb={2} border="1px solid #EBEBEB" borderRadius={2}>
      <Stack direction="row" gap={2}>
        <Image
          src={
            cartItem.mockup_images?.[cartItem.product.id] ||
            cartItem.product.image ||
            cartItem.product.blank_image ||
            NoImageAvailable
          }
          width={44}
          height={44}
          alt="Cart Item"
          responsive={[{ size: { height: 44, width: 44 } }]}
          options={{ fit: 'inside' }}
          dprVariants={[1, 3]}
          loading="lazy"
        />
        <Box sx={{ width: '100%', mb: '12px' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="start" mb={1}>
            <Box>
              <Tooltip
                title={cartItem?.product.name}
                placement="bottom"
                arrow
                sx={{ pointerEvents: 'auto !important' }}
              >
                {cartItem?.product.id === undefined || cartItem?.product.id < 0 ? (
                  <Typography variant="body2" fontWeight={500} maxWidth="50vw" sx={{ ellipsisStyle }}>
                    {cartItem.product.name}
                  </Typography>
                ) : (
                  <MuiRemixLink
                    href={`/shop/${cartItem.product.slug}`}
                    color="inherit"
                    underline="none"
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      '&:hover': { color: 'primary.main' },
                      maxWidth: '50vw',
                      ...ellipsisStyle,
                      display: 'block',
                    }}
                  >
                    {cartItem.product.name}
                  </MuiRemixLink>
                )}
              </Tooltip>
              {(cartItem?.configuration_type === ConfigurationTypes.blank ||
                cartItem?.configuration_type === ConfigurationTypes.sample) && (
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontWeight: 400, fontSize: '12px', lineHeight: '12px' }}
                >
                  {cartItem.configuration_type}
                </Typography>
              )}
            </Box>
            <fetcher.Form method="post" action="/cart/?index" style={{ lineHeight: 0 }}>
              <input type="hidden" name="product" value={getCartItemId(cartItem)} />
              <input type="hidden" name="configuration_type" value={cartItem.configuration_type} />
              <input type="hidden" name="_action" value="delete" />
              <IconButton type="submit" aria-label="delete" sx={{ fontSize: 20, p: 0, width: 20, height: 20 }}>
                <DeleteOutlineIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </fetcher.Form>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Stack justifyContent="space-between">
              {cartItem.type === 'Pack' ? (
                <Typography variant="caption" component="p">
                  Variants: {cartItem.parts.length}
                </Typography>
              ) : (
                <Stack>
                  <Typography variant="caption" component="p">
                    Variants:{' '}
                    {cartItem.parts
                      .map((part) => part.attributes?.find((attr) => attr.name === 'size')?.value)
                      .join(', ')}
                  </Typography>
                </Stack>
              )}
              <Typography variant="caption" component="p" sx={{ lineHeight: '12px' }}>
                Total Qty: {getCartItemTotalQuantity(cartItem)}
              </Typography>
            </Stack>
            <Stack justifyContent="space-between" textAlign="right">
              <Typography variant="caption" component="p">
                Estimated Subtotal:
              </Typography>
              <Typography variant="subtitle1" component="p" sx={{ color: 'text.primary', lineHeight: '16px' }}>
                {getFormattedPrice(getCartItemTotalPrice(cartItem) / 100)}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Stack>

      {cartItem?.configuration_type !== ConfigurationTypes.sample && (
        <Stack direction="row" gap={3}>
          <Button
            variant="text"
            disableRipple
            sx={{
              p: 0,
              color: 'primary.main',
              fontSize: 16,
              minWidth: 0,
              '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
            }}
            onClick={onOpenDetails}
          >
            Edit
          </Button>
          {/* TOFIX */}
          {/* {cartItem?.configuration_type === ConfigurationTypes.decorated && (
            <Button
              type="submit"
              name="_action"
              value="addDesignLab"
              variant="text"
              disableRipple
              sx={{
                p: 0,
                color: 'primary.main',
                fontSize: 16,
                '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
              }}
            >
              {Object.keys(cartItem?.mockup_images ?? {}).length > 0 ? 'Edit design' : 'Start designing'}
            </Button>
          )} */}
        </Stack>
      )}
    </Box>
  );
}

type VariantHeaderDesktopProps = {
  cartItem: CartItem | null;
  deleteIcon?: boolean;
  startDesigningBtn?: JSX.Element;
  bottomComponent?: JSX.Element;
};
export function VariantHeaderDesktop({
  cartItem,
  deleteIcon = false,
  bottomComponent,
  startDesigningBtn,
}: VariantHeaderDesktopProps) {
  const fetcher = useFetcher();
  const isBlankOrSample =
    cartItem?.configuration_type === ConfigurationTypes.blank ||
    cartItem?.configuration_type === ConfigurationTypes.sample;

  return (
    <Stack direction="row" mx={2} gap={2}>
      <Image
        src={
          cartItem?.mockup_images?.[cartItem?.product.id] ||
          cartItem?.product.image ||
          cartItem?.product.blank_image ||
          NoImageAvailable
        }
        key={cartItem?.product.image}
        alt="Product"
        style={{ width: 136, height: 136 }}
        responsive={[{ size: { height: 136, width: 136 } }]}
        options={{ fit: 'inside' }}
        dprVariants={[1, 3]}
        loading="lazy"
      />
      <Stack justifyContent="space-between" width="100%">
        <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1.5} mb={isBlankOrSample ? 3 : 6}>
          <Box>
            <Tooltip title={cartItem?.product.name} placement="bottom" arrow sx={{ pointerEvents: 'auto !important' }}>
              {cartItem?.product.id === undefined || cartItem?.product.id < 0 ? (
                <Typography variant="h5" sx={{ color: '#0F172A', fontWeight: 600, maxWidth: 390, ...ellipsisStyle }}>
                  {cartItem?.product.name}
                </Typography>
              ) : (
                <MuiRemixLink
                  href={`/shop/${cartItem?.product.slug}`}
                  color="inherit"
                  underline="none"
                  variant="h5"
                  sx={{
                    color: '#0F172A',
                    fontWeight: 600,
                    '&:hover': { color: 'primary.main' },
                    maxWidth: 390,
                    ...ellipsisStyle,
                    display: 'block',
                  }}
                >
                  {cartItem?.product.name}
                </MuiRemixLink>
              )}
            </Tooltip>
            {isBlankOrSample && (
              <Typography variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: 400, fontSize: '16px' }}>
                {cartItem?.configuration_type}
              </Typography>
            )}
          </Box>
          <Stack direction="row" alignItems="center">
            {startDesigningBtn}
            {deleteIcon && (
              <fetcher.Form method="post" action="/cart/?index" style={{ marginLeft: '12px' }}>
                <input type="hidden" name="product" value={cartItem !== null ? getCartItemId(cartItem) : ''} />
                <input type="hidden" name="configuration_type" value={cartItem?.configuration_type} />
                <input type="hidden" name="_action" value="delete" />
                <IconButton type="submit" aria-label="delete" sx={{ p: 0 }}>
                  <DeleteOutlineIcon />
                </IconButton>
              </fetcher.Form>
            )}
          </Stack>
        </Stack>

        <Stack direction="row" justifyContent="space-between">
          <Stack>
            <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 400, fontSize: '16px' }}>
              Variants: {cartItem?.parts.length}
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.primary', fontSize: '20px' }}>
              Qty: {cartItem !== null ? getCartItemTotalQuantity(cartItem) : ''}
            </Typography>
          </Stack>
          <Stack>
            <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 500 }}>
              Estimated Subtotal:
            </Typography>
            <Typography sx={{ color: 'text.primary', fontWeight: 500, fontSize: '20px' }}>
              {cartItem !== null ? getFormattedPrice(getCartItemTotalPrice(cartItem) / 100) : ''}
            </Typography>
          </Stack>
        </Stack>
        {bottomComponent}
      </Stack>
    </Stack>
  );
}

type VariantMobileProps = {
  product: CartItem | null;
  setAnchor: React.Dispatch<React.SetStateAction<boolean>>;
};

export function VariantMobile({ product, setAnchor }: VariantMobileProps) {
  return (
    <Box sx={{ display: { xs: 'grid', md: 'none' }, gridTemplateRows: '72px 130px 1fr 60px', height: '100vh' }}>
      <Stack p={3} borderBottom="1px solid #009dc8">
        <ArrowBackIcon sx={{ color: '#757575' }} onClick={() => setAnchor(false)} />
      </Stack>
      <Stack direction="row" borderBottom="1px solid #E0E0E0" p={2}>
        <Image
          src={
            product?.mockup_images?.[product?.product.id] ||
            product?.product.image ||
            product?.product.blank_image ||
            NoImageAvailable
          }
          alt="Product"
          style={{ width: 97, height: 97, borderRadius: 2 }}
          responsive={[{ size: { height: 97, width: 97 } }]}
          options={{ fit: 'inside' }}
          dprVariants={[1, 3]}
          loading="lazy"
        />
        <Stack justifyContent="space-between" ml={1.5}>
          <Tooltip title={product?.product.name} placement="bottom" arrow sx={{ pointerEvents: 'auto !important' }}>
            {product?.product.id === undefined || product?.product.id < 0 ? (
              <Typography
                variant="subtitle1"
                sx={{
                  color: '#0F172A',
                  fontWeight: 700,
                  mt: 0.5,
                  maxWidth: '50vw',
                  ...ellipsisStyle,
                  display: 'block',
                }}
              >
                {product?.product.name}
              </Typography>
            ) : (
              <MuiRemixLink
                href={`/shop/${product?.product.slug}`}
                variant="subtitle1"
                color="inherit"
                underline="none"
                sx={{
                  color: '#0F172A',
                  fontWeight: 700,
                  mt: 0.5,
                  '&:hover': { color: 'primary.main' },
                  maxWidth: '50vw',
                  ...ellipsisStyle,
                  display: 'block',
                }}
              >
                {product?.product.name}
              </MuiRemixLink>
            )}
          </Tooltip>

          <Stack gap={0.2}>
            <Typography variant="subtitle2" sx={{ color: 'text.primary', fontSize: '14px', fontWeight: 400 }}>
              Variants: {product?.parts.length ?? 0}
            </Typography>
            <Typography variant="subtitle2" sx={{ color: 'text.primary', fontSize: '14px', fontWeight: 400 }}>
              Total Quantity: {product !== null ? getCartItemTotalQuantity(product) : ''}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <Stack
        sx={{
          backgroundColor: 'white',
          p: 2,
          borderRadius: 2,
          overflowY: 'auto',
          height: '100%',
          maxHeight: '70vh',
          '& > .MuiBox-root + .MuiBox-root': { pt: '16px', borderTop: '1px solid #EBEBEB' },
        }}
      >
        {isCartItemPack(product) &&
          product.parts.map((variant, idx) => (
            <VariantPack
              product={product}
              variant={variant}
              position={idx + 1}
              key={getCartItemPartId(product, variant)}
            />
          ))}
        {isCartItemProduct(product) &&
          product.parts.map((variant) => (
            <VariantProduct product={product} variant={variant} key={getCartItemPartId(product, variant)} />
          ))}
      </Stack>
      <Box sx={{ backgroundColor: '#FAFAFA', p: '16px 24px', display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="subtitle1" sx={{ color: 'text.primary' }}>
          Estimated Subtotal
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            color: 'text.primary',
            display: '-webkit-box',
            overflow: 'hidden',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {product !== null ? getFormattedPrice(getCartItemTotalPrice(product) / 100) : ''}
        </Typography>
      </Box>
    </Box>
  );
}

export function VariantProduct({ product, variant }: { product: CartItem; variant: CartItemProductPart }) {
  const quantityFetcher = useFetcher();
  const fetcher = useFetcher();

  const [currentValue, setCurrentValue] = React.useState<number | string>(variant.quantity || 0);
  const error = !currentValue || Number(currentValue) < 1 ? 'Quantity must be greater than 0' : '';

  const isSample = product.configuration_type === 'Sample';
  function handleChangeQty(evt: React.ChangeEvent<HTMLInputElement>) {
    const { value } = evt.target;
    if (value && product.configuration_type === 'Sample' && +value > product.product.max_sample_quantity) return;
    const parsedQty = parseInt(value, 10);
    if (parsedQty === 0 && value !== '') return;
    setCurrentValue(value ? parsedQty : '');
  }
  function handleResetQty() {
    setCurrentValue(variant.quantity ? variant.quantity : 0);
  }
  const cartLatestQty = Number(quantityFetcher.submission?.formData.get('quantity')?.toString() ?? variant.quantity);
  const removeFetcher = useFetcher();
  const navigation = useNavigation();
  const isRemoving = removeFetcher.state !== 'idle' || navigation.state !== 'idle';

  // Syncs currentValue with productDrawer quantity
  React.useEffect(() => {
    if (variant.quantity) {
      setCurrentValue(variant.quantity);
    }
  }, [variant]);

  return (
    <Box pb={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        {/* Show Product Attribute */}
        {variant.attributes?.map((attribute, idx, array) => {
          if (attribute.name === 'color' && array.length > 1) return null;
          // show color attribute if product doesn´t have another attribute
          if (attribute.name === 'color' && array.length === 1)
            return (() => {
              const color = product.product.available_attributes.colors.find(
                (color) => attribute?.value === color.name
              ) as TColor;
              return color !== undefined && color?.hex ? <ColorDisplay color={color} /> : null;
            })();

          return (
            <Typography
              key={attribute.value}
              variant="subtitle2"
              sx={{ color: 'text.primary', fontSize: '16px', textTransform: 'capitalize' }}
            >
              {attribute.name}:
              <Typography component="span" variant="subtitle2" fontWeight={400} ml={0.5} fontSize="16px">
                {attribute.value}
              </Typography>
            </Typography>
          );
        })}
        <fetcher.Form method="post" action="/cart/?index" replace>
          <input type="hidden" name="product" value={getCartItemId(product)} />
          <input type="hidden" name="configuration_type" value={product.configuration_type} />
          <input type="hidden" name="part" value={getCartItemPartId(product, variant)} />
          <input type="hidden" name="_action" value="deletePart" />
          <IconButton
            type="submit"
            name="_action"
            value="delete"
            aria-label="delete"
            sx={{ width: '24px', height: '24px' }}
          >
            <DeleteOutlineIcon sx={{ color: '#757575', cursor: 'pointer' }} />
          </IconButton>
        </fetcher.Form>
      </Stack>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: { xs: 'end', md: 'center' },
        }}
      >
        <Stack direction="row" alignItems="end">
          <quantityFetcher.Form method="post" action="/cart/?index" replace>
            <input type="hidden" name="part" value={getCartItemPartId(product, variant)} />
            <input type="hidden" name="cartItem" value={getCartItemId(product)} />
            <input type="hidden" name="productId" value={product.product.id} />
            <input type="hidden" name="configuration_type" value={product.configuration_type} />
            <FormControl sx={{ flexDirection: 'row', alignItems: 'center' }}>
              <Typography
                component="label"
                variant="subtitle2"
                sx={{ color: 'text.secondary', fontWeight: 400 }}
                htmlFor="quantity"
              >
                Qty:
              </Typography>
              <LightTooltip title={error ? <FormHelperText error>{error}</FormHelperText> : ''}>
                <TextField
                  name="quantity"
                  id="quantity"
                  variant="outlined"
                  size="small"
                  type="number"
                  value={currentValue ?? ''}
                  onChange={handleChangeQty}
                  error={Boolean(error)}
                  helperText={isSample ? `Max: ${product.product.max_sample_quantity}` : ''}
                  disabled={isRemoving}
                  inputProps={{
                    min: 1,
                    max:
                      product.configuration_type === ConfigurationTypes.sample
                        ? product.product.max_sample_quantity
                        : 5000,
                  }}
                  sx={{
                    maxWidth: { xs: 100, md: 90 },
                    mx: 1,
                    height: 32,
                    '.MuiInputBase-root': { height: 32 },
                  }}
                />
              </LightTooltip>
              <Fade in={cartLatestQty !== currentValue && !error}>
                <Stack flexDirection="row">
                  <IconButton color="primary" size="small" type="submit" name="_action" value="changeQty">
                    <CheckIcon />
                  </IconButton>
                  <IconButton color="error" size="small" onClick={handleResetQty}>
                    <CancelIcon />
                  </IconButton>
                </Stack>
              </Fade>
            </FormControl>
          </quantityFetcher.Form>
        </Stack>
        <Stack textAlign="right">
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 400 }}>
            Estimated price: {getFormattedPrice(variant.price / 100)}
          </Typography>
          <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
            Estimated Subtotal: {getFormattedPrice((variant.price * variant.quantity) / 100)}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

type VariantPackProps = {
  product: CartItem;
  variant: CartItemPackPart;
  position: number;
};
export function VariantPack({ product, variant, position }: VariantPackProps) {
  const quantityFetcher = useFetcher();
  const fetcher = useFetcher();

  const [currentValue, setCurrentValue] = React.useState<number | string>(variant.quantity || 0);
  const error = Number(currentValue) < 1 ? 'Quantity must be greater than 0' : '';
  function handleChangeQty(evt: React.ChangeEvent<HTMLInputElement>) {
    const qty = evt.target.value;
    const parsedQty = parseInt(qty, 10);
    if (parsedQty === 0 && qty !== '') return;
    setCurrentValue(qty ? parsedQty : '');
  }
  function handleResetQty() {
    setCurrentValue(variant.quantity ? variant.quantity : 1);
  }
  const cartLatestQty = Number(quantityFetcher.submission?.formData.get('quantity')?.toString() ?? variant.quantity);
  const removeFetcher = useFetcher();
  const navigation = useNavigation();
  const isRemoving = removeFetcher.state !== 'idle' || navigation.state !== 'idle';

  // Syncs currentValue with productDrawer quantity
  React.useEffect(() => {
    if (variant.quantity) {
      setCurrentValue(variant.quantity);
    }
  }, [variant]);

  const pack = product.product;
  const fillerColor = product.parts[0].items.find((product) => product.product_id === pack.filler?.id);
  const containerColor = product.parts[0].items.find((product) => product.product_id === pack.container?.id);
  const fillerColorAttribute = pack.filler?.available_attributes?.colors.find(
    (combination) => combination.name === fillerColor.attributes[0].value
  );
  const containerColorAttribute = pack.container?.available_attributes?.colors.find(
    (combination) => combination.name === containerColor.attributes[0].value
  );

  return (
    <Box pb={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
          Variant {position}
        </Typography>
        <fetcher.Form method="post" action="/cart/?index" replace>
          <input type="hidden" name="product" value={getCartItemId(product)} />
          <input type="hidden" name="configuration_type" value={product.configuration_type} />
          <input type="hidden" name="part" value={getCartItemPartId(product, variant)} />
          <input type="hidden" name="_action" value="deletePart" />
          <IconButton
            type="submit"
            name="_action"
            value="delete"
            aria-label="delete"
            sx={{ width: '24px', height: '24px' }}
          >
            <DeleteOutlineIcon sx={{ color: '#757575', cursor: 'pointer' }} />
          </IconButton>
        </fetcher.Form>
      </Stack>
      <DraggableCarousel
        sx={{
          mb: '16px',
          gap: '8px',
          scrollbarWidth: 'none',
        }}
      >
        <>
          {pack.items.map((item) => {
            const itemAttributes = variant.items.find((packItem) => packItem.product_id === item.product?.id);

            const sizeAttribute = itemAttributes?.attributes?.find((attribute) => 'size' === attribute.name);

            // get color name from part variant
            const colorAttribute = itemAttributes?.attributes?.find((attribute) => 'color' === attribute.name);
            // get product from pack
            const available = pack.items.find((packitem) => packitem.product?.id === item.product?.id);
            // get hex value to display from product
            const color = available?.product?.available_attributes?.colors?.find(
              (color) => colorAttribute?.value === color.name
            ) as TColor;

            return (
              <Stack
                direction="row"
                key={item.product?.id}
                flexDirection="column"
                alignItems="start"
                justifyContent="flex-start"
                gap={1}
                padding={1}
                borderRadius={2}
                width="81px"
                height="133px"
                border="1px solid #EBEBEB"
              >
                <Image
                  width={59}
                  height={59}
                  src={
                    product.mockup_images?.[item.product.id] ||
                    item?.product?.image ||
                    item?.product?.blank_image ||
                    NoImageAvailable
                  }
                  alt={'Pack item'}
                  responsive={[{ size: { height: 59, width: 59 } }]}
                  options={{ fit: 'inside' }}
                  dprVariants={[1, 3]}
                  loading="lazy"
                />
                {color !== undefined && color?.hex && <ColorDisplay color={color} />}
                {sizeAttribute?.value && (
                  <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                    Size:
                    <Typography component="span" variant="subtitle2" fontWeight={400} ml={0.5}>
                      {sizeAttribute?.value}
                    </Typography>
                  </Typography>
                )}
              </Stack>
            );
          })}
          {/* Shows Pack container   */}
          <Box sx={{ width: 81, height: 133, padding: '8px', border: '1px solid #EBEBEB', borderRadius: '8px' }}>
            <Image
              key={pack.container?.image}
              src={product.mockup_images?.[pack.container?.id] ?? pack.container?.image}
              width={59}
              height={59}
              alt={'Pack item'}
              responsive={[{ size: { height: 59, width: 59 } }]}
              options={{ fit: 'inside' }}
              dprVariants={[1, 3]}
              loading="lazy"
            />
            {containerColorAttribute?.hex && <ColorDisplay color={containerColorAttribute as TColor} />}
          </Box>
          {/*  Shows Pack filler  */}
          {pack.filler && (
            <Box sx={{ width: 81, height: 133, padding: '8px', border: '1px solid #EBEBEB', borderRadius: '8px' }}>
              <Image
                key={pack.filler?.image}
                src={product.mockup_images?.[pack.filler?.id] ?? pack.filler?.image}
                alt="Pack item"
                width={59}
                height={59}
                responsive={[{ size: { height: 59, width: 59 } }]}
                options={{ fit: 'inside' }}
                dprVariants={[1, 3]}
                loading="lazy"
              />
              {fillerColorAttribute?.hex && <ColorDisplay color={fillerColorAttribute as TColor} />}
            </Box>
          )}
        </>
      </DraggableCarousel>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: { xs: 'end', md: 'center' },
        }}
      >
        <Stack direction="row" alignItems="end">
          <quantityFetcher.Form method="post" action="/cart/?index" replace>
            <input type="hidden" name="part" value={getCartItemPartId(product, variant)} />
            <input type="hidden" name="productId" value={product.product.id} />
            <input type="hidden" name="cartItem" value={getCartItemId(product)} />
            <input type="hidden" name="configuration_type" value={product.configuration_type} />

            <FormControl sx={{ flexDirection: 'row', alignItems: 'center' }}>
              <Typography
                component="label"
                variant="subtitle2"
                sx={{ color: 'text.secondary', fontWeight: 400 }}
                htmlFor="quantity"
              >
                Qty:
              </Typography>
              <LightTooltip title={error ? <FormHelperText error>{error}</FormHelperText> : ''}>
                <TextField
                  name="quantity"
                  id="quantity"
                  variant="outlined"
                  size="small"
                  type="number"
                  value={currentValue}
                  onChange={handleChangeQty}
                  error={Boolean(error)}
                  disabled={isRemoving || product.configuration_type === ConfigurationTypes.sample}
                  inputProps={{ min: 0 }}
                  sx={{
                    maxWidth: { xs: 100, md: 90 },
                    mx: 1,
                    height: 32,
                    '.MuiInputBase-root': { height: 32 },
                  }}
                />
              </LightTooltip>
              <Fade in={cartLatestQty !== currentValue && !error}>
                <Stack flexDirection="row">
                  <IconButton color="primary" size="small" type="submit" name="_action" value="changeQty">
                    <CheckIcon />
                  </IconButton>
                  <IconButton color="error" size="small" onClick={handleResetQty}>
                    <CancelIcon />
                  </IconButton>
                </Stack>
              </Fade>
            </FormControl>
          </quantityFetcher.Form>
        </Stack>
        <Stack textAlign="right">
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 400, mb: '4px' }}>
            Estimated price: {getFormattedPrice(variant.price / 100)}
          </Typography>
          <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
            Estimated Subtotal: {getFormattedPrice((variant.price * variant.quantity) / 100)}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

export function ColorDisplay({ color }: { color: TColor }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: '4px',
        alignItems: 'center',
        '> div': {
          mr: 0,
          ' > button': {
            minWidth: '14.30px',
            height: '14.30px',
            padding: 0,
          },
        },
      }}
    >
      <Typography variant="subtitle2" sx={{ color: 'text.primary', lineHeight: '11px' }}>
        Color:
      </Typography>
      <Color disabled disabledLabel={color.name} color={color} border="1px solid rgba(0, 0, 0, 1)" />
    </Box>
  );
}

function getCartItemTotalPrice(cartItem: CartItem): number {
  //Typescript error with union types
  //https://github.com/microsoft/TypeScript/issues/36390
  // @ts-ignore
  return cartItem.parts.reduce((total, part) => total + part.quantity * part.price, 0);
}

export function getCartItemTotalQuantity(cartItem: CartItem): number {
  //Typescript error with union types
  //https://github.com/microsoft/TypeScript/issues/36390
  // @ts-ignore
  return cartItem.parts.reduce((total, part) => total + part.quantity, 0);
}
